/**
 * CustomCursor module
 *
 * @package AGDS
 * @category Modules
 * @module CustomCursor
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class CustomCursor {
    /**
     * Сonstructor
     */
    constructor() {
        this.cursorElement = null;
        this.cursorLabelElement = null;

        // Mouse target coordinates
        this.mouseX = -100;
        this.mouseY = -100;

        // Current cursor coordinates (for smooth interpolation / LERP)
        this.currentX = -100;
        this.currentY = -100;

        this.rafId = null;

        // Initialize custom cursor on startup
        this.initCustomCursor();
    }

    /**
     * Initializes a custom interactive cursor for fine pointers
     * while respecting user motion preferences (prefers-reduced-motion).
     *
     * @returns {void}
     */
    initCustomCursor() {
        this.cursorElement = document.querySelector("#cursor");
        if (!this.cursorElement) return;

        // Check user preferences for reduced motion and fine pointer devices (mouse/stylus)
        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const hasFinePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

        // Select the inner label element for the cursor
        this.cursorLabelElement = this.cursorElement.querySelector(".cursor-label");

        // Determine whether to activate the custom cursor
        const useCursor = hasFinePointer && !prefersReducedMotion;

        if (useCursor) {
            document.documentElement.classList.add("has-cursor");

            /**
             * Handles pointer movement to update cursor position and interactive states.
             * @param {PointerEvent} event - The native pointer event.
             */
            this.handlePointerMove = (event) => {
                // Process only mouse pointer events
                if (event.pointerType !== "mouse") return;

                this.mouseX = event.clientX;
                this.mouseY = event.clientY;
                this.cursorElement.classList.add("on");

                const target = event.target;

                // Check for view elements or interactive controls
                const viewTarget = target.closest("[data-cursor-label]");
                const linkTarget =
                    !viewTarget && target.closest("[data-cursor-hover], a[href], button, input[type='checkbox']");

                // Toggle structural CSS classes
                this.cursorElement.classList.toggle("is-view", !!viewTarget);
                this.cursorElement.classList.toggle("is-link", !!linkTarget);

                // Update cursor label text if label element exists
                if (this.cursorLabelElement) {
                    this.cursorLabelElement.textContent = viewTarget?.dataset.cursorLabel || "";
                }

                // Apply theme/modifier classes based on hovered elements
                this.cursorElement.classList.toggle(
                    "on-accent",
                    !!target.closest(".bg-accent"),
                );
                this.cursorElement.classList.toggle(
                    "on-black",
                    !!target.closest(".bg-dark, .bg-black"),
                );
                this.cursorElement.classList.toggle(
                    "on-light",
                    !!target.closest(".bg-light, .bg-white"),
                );
            };

            // Handlers for mouse leave/enter on the document viewport
            this.handleMouseLeave = () => this.cursorElement.classList.remove("on");
            this.handleMouseEnter = () => this.cursorElement.classList.add("on");

            // Register event listeners
            window.addEventListener("pointermove", this.handlePointerMove, { passive: true });
            document.addEventListener("mouseleave", this.handleMouseLeave);
            document.addEventListener("mouseenter", this.handleMouseEnter);

            // Start the smooth animation loop
            this.startCursorLoop();
        }
    }

    /**
     * Starts the requestAnimationFrame loop to smoothly interpolate (LERP)
     * the cursor position towards the mouse coordinates.
     */
    startCursorLoop() {
        const render = () => {
            // Linear interpolation (0.38 controls the smoothness/lag of the cursor)
            this.currentX += (this.mouseX - this.currentX) * 0.38;
            this.currentY += (this.mouseY - this.currentY) * 0.38;

            if (this.cursorElement) {
                // Apply hardware-accelerated transform to move the cursor
                this.cursorElement.style.transform = `translate3d(${this.currentX}px, ${this.currentY}px, 0)`;
            }

            this.rafId = requestAnimationFrame(render);
        };

        this.rafId = requestAnimationFrame(render);
    }

    /**
     * Cleans up event listeners and animation frames.
     */
    destroy() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }
        window.removeEventListener("pointermove", this.handlePointerMove);
        document.removeEventListener("mouseleave", this.handleMouseLeave);
        document.removeEventListener("mouseenter", this.handleMouseEnter);
    }
}
